// Import React dependencies.
import React, {
  PropsWithChildren, useEffect, useState,
} from 'react';
import { RenderElementProps, useReadOnly } from 'slate-react';
import classNames from 'classnames';
import { PlateRenderNodeProps } from '@udecode/plate-core';
import { Data } from '@styled-icons/boxicons-regular';
import {
  useFloating, shift, autoUpdate,
} from '@floating-ui/react-dom';
import { QuestionCircle } from '@styled-icons/bootstrap';
import { DreamBlock, DreamValue, RichTextContentElement as RichTextContentElementType } from '@typedream/core';
import styled from 'styled-components';
import { DEFAULT_NAVBAR_HEIGHT } from '../../../../constants';
import { DreamEditor } from '../../../../types';
import { getTPlugin, useTPlateEditorRef } from '../../../../plate';
import { CollectionDataPluginOptions } from '../../types';
import { KEY_COLLECTION_CONNECT_DATA } from '../../../../pluginKeys';
import { getWidthStyle } from '../../../../utils/getWidthStyle';

const DivReadOnlyContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
`;

const DivContainer = styled.div`
    width: 100%;
    box-sizing: border-box;
    min-height: 100px;
    height: fit-content;
    display: flex;
    align-items: center;
    justify-content: center;
    user-select: none !important;
    background-color: var(--background);
    color: var(--accent);
    border-radius: 10px;
    cursor: pointer;
    border: 3px dashed var(--accent);
    position: relative;
`;

const DivPreviewContainer = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
`;

const PlaceholderImage = styled.img<{ imageDark?: string; image: string; }>`
  content:url(${(props) => props.image});  
  
  [data-theme="dark"] &{
    content:url(${(props) => props.imageDark});
  }
`;

const DivCenterText = styled.div`
    position: absolute;
    margin: 40px;
    display: flex;
    flex-direction: column;
    max-width: 400px;
    align-items: center;
    justify-content: center;
    padding: 20px;
    border-radius: 10px;
    background-color: var(--background-button);
    border: 1px dashed var(--accent);
    box-shadow: var(--shadow-modal);
    text-align: center;
`;

const DivTitle = styled.div`
    display: flex !important;
    align-items: center !important;
`;

const StyledQuestionCircle = styled(QuestionCircle)`
    cursor: pointer;
    margin-left: 0.25rem !important;
`;

const DivSubtitle = styled.div`
    font-size: 15px;
    color: var(--text-lighter);
    margin-top: 0.75rem !important;
    font-weight: 400 !important;
    width: 100%;
`;

const DivSpanTag = styled.span`
    padding: 2px 5px;
    font-size: 15px;
    color: var(--text-lighter);
    background-color: var(--background-highlight);
    border-radius: 2px;
    margin-right: 2px;
    line-height: inherit;
    vertical-align: middle;
    user-select: none !important;
    -webkit-user-select: none !important;
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 0.75rem !important;
`;

const DivTooltipTitle = styled.div`
    letter-spacing: -0.5px;
    font-size: 1.25rem !important;
    font-weight: 700 !important;
    color: var(--text);
    margin-bottom: 0.5rem !important;
    width: 100%;
    text-align: left !important;
`;

const DivBoxBorderTooltip = styled.div`
    background-color: var(--background-button);
    box-shadow: var(--shadow-modal);
    border: 1px solid var(--border-modal);
    border-radius: 4px;
    cursor: pointer;

    padding: 1.5rem !important;
    margin-right: 0.5rem !important;
`;

const DivSubtitleTooltip = styled.div`
    font-size: 15px;
    font-weight: 500;
    color: var(--text-lighter);
    margin-top: 0.75rem !important;
    text-align: left !important;
`;

function RichTextContentElement(props:
{ element: DreamBlock } & PropsWithChildren<RenderElementProps> &
PlateRenderNodeProps<DreamValue, DreamEditor>) {
  const {
    children, attributes, element,
  } = props;

  const { width, width_max } = element as RichTextContentElementType;

  const editor = useTPlateEditorRef();
  const readOnly = useReadOnly();
  const { options } = getTPlugin<CollectionDataPluginOptions>(editor, KEY_COLLECTION_CONNECT_DATA);
  const { showSchema } = options as CollectionDataPluginOptions;
  const [tooltipOpen, settooltipOpen] = useState(false);
  const imagePlaceholderDark = '/images/rich_text_content_placeholder_dark.png';
  const imagePlaceholder = '/images/rich_text_content_placeholder.png';
  const {
    x, y, reference, floating, strategy, update, refs,
  } = useFloating({
    strategy: 'fixed',
    placement: 'left-end',
    middleware: [shift()],
  });

  useEffect(() => {
    if (!refs.reference.current || !refs.floating.current) {
      return;
    }

    // eslint-disable-next-line
      return autoUpdate(
      refs.reference.current,
      refs.floating.current,
      update,
    );
  }, [refs.reference, refs.floating, update]);

  if (!showSchema && readOnly && children) {
    return (
      <DivReadOnlyContainer
        {...attributes}
      >
        {children}
      </DivReadOnlyContainer>
    );
  }

  if (!showSchema && readOnly && !children) {
    return (
      // eslint-disable-next-line react/jsx-no-useless-fragment
      <></>
    );
  }

  return (
    <DivContainer
      style={getWidthStyle(width, width_max)}
      {...attributes}
    //   className={classNames(className)}
      contentEditable={false}
    >
      <DivPreviewContainer>
        <PlaceholderImage
          image={imagePlaceholder}
          imageDark={imagePlaceholderDark}
          alt="Placeholder for Rich Text Content"
          style={{ width: '100%', height: '100%' }}
        />
        <DivCenterText
          role="none"
        >
          <DivTitle className="is-flex is-align-items-center">
            Rich Text Content
            <StyledQuestionCircle
              size={18}
              className="ml-1 with-cursor-pointer"
              ref={reference}
              onMouseEnter={() => settooltipOpen(true)}
              onMouseLeave={() => settooltipOpen(false)}
            />
          </DivTitle>
          <DivSubtitle className="is-subtitle mt-3 has-text-weight-normal">
            This block will be replaced with the rich text content you filled in for each item.
          </DivSubtitle>
          <DivSpanTag
            className={classNames('mt-3')}
            contentEditable={false}
            style={{
              userSelect: 'none',
            }}
          >
            <Data size={13} />
            rich text content
          </DivSpanTag>
        </DivCenterText>
      </DivPreviewContainer>
      <span style={{ display: 'none' }}>
        {children}
      </span>
      {tooltipOpen && (
        <DivBoxBorderTooltip
          ref={floating}
          style={{
            position: strategy,
            top: Math.max(y as number, DEFAULT_NAVBAR_HEIGHT) ?? '',
            left: x ?? '',

            transition: 'all .2s ease',
            width: 500,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
          }}
          className="box-border-only p-5 mr-2"
        >
          <DivTooltipTitle className="is-size-5 has-text-weight-bold with-text-color mb-2 has-fullwidth has-text-left">
            What is Rich Text Content?
          </DivTooltipTitle>
          <img
            src="/images/rich_text_content_explanation.png"
            alt="Rich Text Content Explanation"
            style={{
              width: '100%',
            }}
          />
          <DivSubtitleTooltip className="is-subtitle mt-3 has-fullwidth has-text-left">
            This block will be replaced by the Rich Text Content that you
            fill in for each item. Normally, you&apos;ll use this space
            for your Blog content or something similar.
          </DivSubtitleTooltip>
        </DivBoxBorderTooltip>
      )}
    </DivContainer>
  );
}

export default RichTextContentElement;
